import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { axiosPostCall } from '../api/axiosCall';
import {
  ACCESS_TOKEN,
  API_END_POINT,
  REFRESH_TOKEN,
  ROUTES,
} from '../constant/constants';
import {
  deleteCookie,
  getToken,
  setCookieWithExpiration,
} from '../utils/authUtils';
import {
  getFCMToken,
  requestNotificationPermission,
  isFCMSupported,
} from '../utils/fcmUtils';

export const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [accessToken, setAccessToken] = useState(
    getToken(ACCESS_TOKEN) || null,
  );
  const [refreshToken, setRefreshToken] = useState(
    getToken(REFRESH_TOKEN) || null,
  );
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [fcmToken, setFcmToken] = useState(null);

  // Check authentication status on mount
  useEffect(() => {
    const hasValidTokens = accessToken && refreshToken;
    setIsAuthenticated(hasValidTokens);
  }, [accessToken, refreshToken]);

  const updateTokens = useCallback((newAccessToken, newRefreshToken) => {
    setAccessToken(newAccessToken);
    setRefreshToken(newRefreshToken);
    setIsAuthenticated(!!(newAccessToken && newRefreshToken));

    // Update cookies with proper expiration
    if (newAccessToken) {
      setCookieWithExpiration(ACCESS_TOKEN, newAccessToken, 1); // 1 day
    }
    if (newRefreshToken) {
      setCookieWithExpiration(REFRESH_TOKEN, newRefreshToken, 7); // 7 days
    }
  }, []);

  const clearTokens = useCallback(() => {
    setAccessToken(null);
    setRefreshToken(null);
    setIsAuthenticated(false);
    setFcmToken(null);
    // Clear cookies
    deleteCookie(ACCESS_TOKEN);
    deleteCookie(REFRESH_TOKEN);
  }, []);

  // Get FCM token (no separate API call)
  const getFCMTokenForLogin = useCallback(async () => {
    try {
      if (!isFCMSupported()) {
        console.log('FCM not supported in this environment');
        return null;
      }

      // Request notification permission
      const permission = await requestNotificationPermission();
      if (permission !== 'granted') {
        console.log('Notification permission not granted');
        return null;
      }

      // Get FCM token
      const token = await getFCMToken();
      if (token) {
        setFcmToken(token);
        return token;
      }
      return null;
    } catch (error) {
      console.error('Error getting FCM token:', error);
      return null;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      // Call logout API if we have a refresh token
      if (refreshToken) {
        await axiosPostCall({
          apiEndPoint: API_END_POINT.LOGOUT,
          header: {
            'Content-Type': 'application/json',
            'Client-type': 'admin',
          },
          body: {
            refreshToken: refreshToken,
          },
        });
      }
    } catch (error) {
      // Even if logout API fails, we should still clear local tokens
      console.error('Logout API call failed:', error);
    } finally {
      // Always clear tokens and redirect
      clearTokens();
      window.location.href = ROUTES.LOGIN;
    }
  }, [clearTokens, refreshToken]);

  const contextValue = useMemo(
    () => ({
      accessToken,
      refreshToken,
      isAuthenticated,
      fcmToken,
      updateTokens,
      clearTokens,
      logout,
      getFCMTokenForLogin,
    }),
    [
      accessToken,
      refreshToken,
      isAuthenticated,
      fcmToken,
      updateTokens,
      clearTokens,
      logout,
      getFCMTokenForLogin,
    ],
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export default AuthProvider;
